import React, { useEffect, useMemo, useState } from "react";
import { getAmadeusPricing } from "../Services/AmadeusApi";
import { useLocation, useNavigate } from "react-router-dom";
import Loading from "../../Loading";
import moment from "moment";
import {
  Luggage,
  Info,
  AccessTime,
  Description,
  CheckCircleOutline,
} from "@mui/icons-material";
import convertNumbThousand from "../../../utils/convertNumbThousand";
import { getCarrierLogo, getCarrierInfo } from "../../../utils/flightInfo";
import customTime from "../../../utils/customTime";
import seat from "../../../images/seat.png";
import ButtonPrimary from "../../Button/ButtonPrimary";
import { toast } from "react-toastify";
import {
  USER_ROLE_EMPLOYEE,
  USER_ROLE_SUPERAGENT,
} from "../../../common/constants";
import { useSelector } from "react-redux";

const AmadeusFlightPricing = () => {
  const {
    offer,
    charges,
    queryURL,
    isRoundTrip,
    rateDifference,
    amaClientRef,
    query,
  } = useLocation().state;

  const navigate = useNavigate();
  const [pricingData, setPricingData] = useState(null);
  const [loading, setLoading] = useState(true);

  const { user } = useSelector((state) => state.auth);

  const isSuper = [USER_ROLE_SUPERAGENT, USER_ROLE_EMPLOYEE].includes(
    user?.role,
  );

  useEffect(() => {
    if (!isSuper && charges <= 0) {
      toast.error("Network Error, Please try again");
      navigate(-1);
    }
  }, [charges, isSuper, navigate]);

  useEffect(() => {
    const fetchAmadeusPricing = async () => {
      try {
        setLoading(true);
        const response = await getAmadeusPricing({
          amaClientRef,
          flightOffers: offer,
          include: "detailed-fare-rules",
          // include: "other-services",
        });
        setPricingData(response);
      } catch (error) {
        console.error("Pricing Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAmadeusPricing();
  }, [amaClientRef, offer]);

  const fareRuleBlocks = useMemo(() => {
    const inc = pricingData?.included;
    if (!inc || typeof inc !== "object") return [];
    const block =
      inc["detailed-fare-rules"] ??
      inc.detailedFareRules ??
      inc.detailed_fare_rules;
    if (!block || typeof block !== "object") return [];
    return Object.entries(block)
      .sort(([a], [b]) => {
        const na = Number(a);
        const nb = Number(b);
        if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
        return String(a).localeCompare(String(b));
      })
      .map(([ruleKey, value]) => ({
        ruleKey,
        ...(value && typeof value === "object" ? value : {}),
      }));
  }, [pricingData]);

  if (loading) return <Loading />;
  if (!pricingData)
    return (
      <div className="py-24 mx-4 mt-10 text-xl font-black text-center bg-white rounded-2xl border shadow-sm text-neutral-900 border-neutral-100 lg:mx-24">
        <Info className="!text-5xl text-amber-500 mb-4" />
        <p>Pricing failed. Please try again.</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-6 text-sm font-bold text-primary-6000 hover:underline"
        >
          Go back to search
        </button>
      </div>
    );

  const flightOffer = pricingData?.data?.flightOffers?.[0];
  const price = flightOffer?.price;
  const itineraries = flightOffer?.itineraries;
  const travelerPricings = flightOffer?.travelerPricings;
  const requirements = pricingData?.data?.bookingRequirements;

  const formatPrice = (amount) => {
    return convertNumbThousand(parseFloat(amount) * (rateDifference || 1));
  };

  const formatDurationMinutes = (totalMinutes) => {
    const minutes = Math.max(0, Number(totalMinutes || 0));
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (!h) return `${m}m`;
    if (!m) return `${h}h`;
    return `${h}h ${m}m`;
  };

  const durationBetween = (startAt, endAt) => {
    if (!startAt || !endAt) return "";
    return formatDurationMinutes(
      moment(endAt).diff(moment(startAt), "minutes"),
    );
  };

  const handleContinue = () => {
    navigate(`/amadeus-booking?${queryURL}`, {
      state: {
        offer: flightOffer,
        itineraries: itineraries.flatMap((it) => it.segments),
        charges: charges || 0,
        queryURL,
        isRoundTrip,
        currency: price.currency,
        rateDifference,
        amaClientRef: pricingData.amaClientRef,
        dictionaries: pricingData.dictionaries,
        channel: "Amadeus",
      },
    });
  };

  const parseDuration = (duration) => {
    if (!duration) return "";
    const hours = Number(duration.match(/(\d+)H/)?.[1] || 0);
    const minutes = Number(duration.match(/(\d+)M/)?.[1] || 0);
    return formatDurationMinutes(hours * 60 + minutes);
  };

  if (!pricingData?.data) {
    return (
      <div className="py-24 mx-4 mt-10 text-xl font-black text-center bg-white rounded-2xl border shadow-sm text-neutral-900 border-neutral-100 lg:mx-24">
        <Info className="!text-5xl text-amber-500 mb-4" />
        <p>Pricing failed. Please try again.</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-6 text-sm font-bold text-primary-6000 hover:underline"
        >
          Go back to search
        </button>
      </div>
    );
  }

  const renderSegment = (segment, idx, allSegments) => {
    const carrierLogo = getCarrierLogo(segment.carrierCode);
    const carrierInfo = getCarrierInfo(segment.carrierCode);
    const fareDetails = travelerPricings?.[0]?.fareDetailsBySegment?.find(
      (f) => f.segmentId === segment.id,
    );
    const baggage = fareDetails?.includedCheckedBags?.quantity ?? 0;
    const cabinClass = fareDetails?.cabin || "";
    const bookingCode = fareDetails?.bookingClass || "";
    const seatsLeft = flightOffer?.numberOfBookableSeats;

    const hasLayover = idx > 0;
    let layoverTime = "";
    if (hasLayover) {
      const prevArrival = moment(allSegments[idx - 1].arrival.at);
      const currentDeparture = moment(segment.departure.at);
      const diff = moment.duration(currentDeparture.diff(prevArrival));
      layoverTime = `${Math.floor(diff.asHours())}h ${diff.minutes()}m`;
    }

    return (
      <div key={segment.id} className="relative">
        {hasLayover && (
          <div className="flex items-center my-2 ml-4">
            <div className="inline-flex gap-2 items-center px-4 py-2 bg-amber-50 rounded-full border border-amber-100">
              <AccessTime className="!text-base text-amber-600" />
              <span className="text-xs font-bold text-amber-800">
                Layover {layoverTime} in {allSegments[idx - 1].arrival.iataCode}
              </span>
            </div>
          </div>
        )}

        <div className="flex flex-col py-4 md:flex-row">
          <div className="flex-shrink-0 w-24 md:w-20 lg:w-24 md:pt-7">
            {carrierLogo ? (
              <img src={carrierLogo} className="w-15" alt="" />
            ) : (
              <div className="flex justify-center items-center h-10 text-xs font-bold rounded w-15 bg-neutral-100">
                {segment.carrierCode}
              </div>
            )}
          </div>

          <div className="flex my-5 md:my-0 lg:w-72">
            <div className="flex flex-col flex-shrink-0 items-center py-2">
              <span className="block w-6 h-6 rounded-full border border-neutral-400"></span>
              <span className="block flex-grow my-1 border-l border-dashed border-neutral-400"></span>
              <span className="block w-6 h-6 rounded-full border border-neutral-400"></span>
            </div>
            <div className="ml-4 space-y-10 text-sm">
              <div className="flex flex-col space-y-1">
                <span className="text-neutral-500">
                  {customTime(segment.departure.at)}
                </span>
                <span className="font-semibold text-black">
                  {segment.departure.iataCode}
                </span>
              </div>
              <div className="flex flex-col space-y-1">
                <span className="text-neutral-500">
                  {customTime(segment.arrival.at)}
                </span>
                <span className="font-semibold text-black">
                  {segment.arrival.iataCode}
                </span>
              </div>
            </div>
          </div>

          <div className="border-l border-neutral-200 md:mx-6 lg:ml-10"></div>

          <ul className="space-y-1 text-sm text-neutral-500 md:space-y-2">
            <li>
              Flight time: <span>{parseDuration(segment.duration)}</span>
            </li>
            <li>Flight Class: {cabinClass}</li>
            <li>
              Flight Number: {carrierInfo?.Name || segment.carrierCode}{" "}
              {segment.carrierCode}
              {segment.number}
            </li>
            <li>Equipment: {segment.aircraft?.code || "N/A"}</li>
            <li>Booking Class: {bookingCode}</li>
            <li className="flex gap-2 items-center">
              <Luggage className="!text-base" />
              <span>
                {baggage} checked bag{baggage === 1 ? "" : "s"}
              </span>
            </li>
            {seatsLeft < 10 && (
              <li className="flex items-center">
                Seat Left:{" "}
                <span className="px-1.5 font-semibold text-white bg-main-900 rounded-full ml-2 mr-1">
                  {seatsLeft}
                </span>{" "}
                <img src={seat} alt="seat" className="w-5 h-5" />
                <span className="ml-1">left</span>
              </li>
            )}
          </ul>
        </div>
      </div>
    );
  };

  return (
    <div className="py-2 min-h-screen bg-neutral-50 md:py-4">
      <div className="container px-4 mx-auto max-w-6xl">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
          {/* Main Itinerary Column */}

          <div className="space-y-6 lg:col-span-3">
            <div className="px-6 py-4 bg-white rounded-2xl border shadow-sm md:p-8 border-neutral-100">
              <div className="flex gap-6 justify-between items-start mb-6">
                <div>
                  <h2 className="mt-1 text-2xl font-medium text-neutral-900">
                    Flight Details
                  </h2>
                </div>
              </div>

              {itineraries?.map((itinerary, itIdx) => (
                <div
                  key={itIdx}
                  className={
                    itIdx > 0 ? "mt-1 pt-1 border-t border-neutral-100" : ""
                  }
                >
                  {(() => {
                    const segs = itinerary?.segments || [];
                    const first = segs[0];
                    const last = segs[segs.length - 1];
                    const stops = Math.max(0, segs.length - 1);
                    const durationText =
                      itinerary?.duration ||
                      durationBetween(first?.departure?.at, last?.arrival?.at);
                    return (
                      <>
                        {/* <div className="flex flex-col gap-3 justify-between p-4 mb-5 rounded-2xl border sm:flex-row sm:items-center bg-neutral-50 border-neutral-100">
                          <div className="flex gap-3 items-center">
                            <div className="flex justify-center items-center w-10 h-10 bg-white rounded-xl border border-neutral-100 text-primary-6000">
                              {itIdx === 0 ? (
                                <FlightTakeoff className="!text-xl" />
                              ) : (
                                <FlightLand className="!text-xl" />
                              )}
                            </div>
                            <div>
                              <p className="text-sm font-bold text-neutral-900">
                                {first?.departure?.iataCode}{" "}
                                <span className="text-neutral-300">→</span>{" "}
                                {last?.arrival?.iataCode}
                              </p>
                              <p className="text-xs text-neutral-500">
                                {moment(first?.departure?.at).format(
                                  "ddd, D MMM"
                                )}{" "}
                                · {customTime(first?.departure?.at)} –{" "}
                                {customTime(last?.arrival?.at)}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2 items-center">
                            
                            <span className="inline-flex items-center px-3 py-1 text-xs font-semibold bg-white rounded-full border border-neutral-100 text-neutral-700">
                              {stops === 0
                                ? "Direct"
                                : `${stops} stop${stops === 1 ? "" : "s"}`}
                            </span>
                          </div>
                        </div> */}
                        <div
                          className="rounded-2xl border card-body border-neutral-100"
                          data-nc-id="FlightCard"
                        >
                          <div
                            className={`relative sm:pr-10`}
                            data-nc-id="FlightCard"
                          >
                            <div className="row">
                              <div className="col-md-2">
                                <div className="flex items-center space-x-2">
                                  <div className="text-xl text-black">
                                    <h1>
                                      {moment(first?.departure?.at).format(
                                        "MMMM Do",
                                      )}
                                    </h1>
                                    <h1>{customTime(first?.departure?.at)}</h1>
                                    <h1 className="p-2 text-lg font-light">
                                      {first?.departure?.iataCode}
                                    </h1>
                                  </div>
                                </div>
                              </div>
                              {/* {"->"} */}
                              <div className="col-md-1">
                                <span className="flex justify-center w-12">
                                  <i className="text-2xl las la-long-arrow-alt-right"></i>
                                </span>
                              </div>
                              <div className="col-md-2">
                                <div className="flex items-center space-x-2">
                                  <div className="text-xl text-black">
                                    <h1>
                                      {moment(last?.arrival?.at).format(
                                        "MMMM Do",
                                      )}
                                    </h1>
                                    <h1>{customTime(last?.arrival?.at)}</h1>
                                    <h1 className="p-2 text-lg font-light">
                                      {last?.arrival?.iataCode}{" "}
                                    </h1>
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="flex items-center px-4 py-3 space-x-2">
                                  <div className="text-lg text-black">
                                    <h1 className="text-sm text-neutral-500 font-normal mt-0.5">
                                      {moment(last?.arrival?.at).diff(
                                        first?.departure?.at,
                                        "hours",
                                      )}{" "}
                                      Hours{" "}
                                      {moment(last?.arrival?.at).diff(
                                        first?.departure?.at,
                                        "minutes",
                                      ) % 60}
                                      Minutes
                                    </h1>
                                    <h1>Direct"</h1>
                                  </div>
                                </div>
                              </div>
                              <div className="space-y-6 col-md-3">
                                <div className="flex justify-between py-2 space-x-2">
                                  <div className="text-sm text-neutral-500 font-normal mt-0.5 text-light">
                                    <h1 className="pb-2">Flight Equipment</h1>
                                    <h1 className="pb-2">Flight Number</h1>
                                  </div>
                                  <div className="">
                                    <h1 className="pb-2">
                                      {first?.aircraft?.code}
                                    </h1>
                                    <h1 className="pb-2">{first?.number}</h1>
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-1">
                                <div className="flex items-center py-2 space-x-2">
                                  <div className="flex-shrink-0 py-2 w-24 lg:w-32 md:ml-10">
                                    <img
                                      src={getCarrierLogo(first?.carrierCode)}
                                      className="w-16"
                                      alt=""
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    );
                  })()}

                  <div className="pl-1 sm:px-8">
                    {itinerary?.segments?.map((seg, sIdx) =>
                      renderSegment(seg, sIdx, itinerary.segments),
                    )}
                  </div>
                </div>
              ))}
            </div>

            {fareRuleBlocks.length > 0 && (
              <div className="p-6 bg-white rounded-2xl border shadow-sm md:p-8 border-neutral-100">
                <h3 className="flex gap-2 items-center text-lg font-bold text-neutral-900">
                  <Description className="text-main-900" />
                  Fare rules
                </h3>
                <p className="mt-1 text-sm text-neutral-500">
                  Detailed fare rules from the carrier pricing response for this
                  itinerary.
                </p>
                <div className="mt-6 space-y-8">
                  {fareRuleBlocks.map((rule) => {
                    const descriptions = rule.fareNotes?.descriptions;
                    const list = Array.isArray(descriptions)
                      ? descriptions
                      : [];
                    return (
                      <div
                        key={rule.ruleKey}
                        className="pt-6 border-t border-neutral-100 first:pt-0 first:border-0"
                      >
                        <div className="flex flex-col gap-1 sm:flex-row sm:flex-wrap sm:items-baseline sm:gap-x-3">
                          {rule.fareBasis ? (
                            <span className="font-mono text-sm font-bold tracking-wide text-neutral-900">
                              {rule.fareBasis}
                            </span>
                          ) : null}
                          {rule.name ? (
                            <span className="text-sm font-semibold text-neutral-700">
                              {rule.name}
                            </span>
                          ) : null}
                          {rule.segmentId ? (
                            <span className="text-xs font-medium uppercase text-neutral-400">
                              Segment ID {rule.segmentId}
                            </span>
                          ) : null}
                        </div>
                        {list.length === 0 ? (
                          <p className="mt-2 text-sm text-neutral-500">
                            No rule text for this fare component.
                          </p>
                        ) : (
                          <div className="mt-4 space-y-2">
                            {list.map((desc, di) => (
                              <details
                                key={`${rule.ruleKey}-${di}`}
                                className="overflow-hidden rounded-xl border border-neutral-100 bg-neutral-50/80 group"
                              >
                                <summary className="px-4 py-3 text-sm font-semibold cursor-pointer list-none text-neutral-900 marker:content-none [&::-webkit-details-marker]:hidden">
                                  <span className="inline-flex gap-2 items-center">
                                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-main-900" />
                                    {desc.descriptionType || "Rule"}
                                  </span>
                                </summary>
                                <div className="px-4 pb-4 -mt-1 text-xs leading-relaxed border-t border-neutral-100/80 text-neutral-700 whitespace-pre-wrap max-h-[min(28rem,70vh)] overflow-y-auto">
                                  {desc.text || "—"}
                                </div>
                              </details>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Smart Requirements Card */}
            <div className="p-6 bg-white rounded-2xl border shadow-sm md:p-8 border-neutral-100">
              <h3 className="flex gap-2 items-center text-lg font-bold text-neutral-900">
                <Info className="text-primary-6000" />
                Booking requirements
              </h3>
              <p className="mt-1 text-sm text-neutral-500">
                Please make sure you have the following ready before continuing.
              </p>
              <div className="grid grid-cols-1 gap-3 mt-5 md:grid-cols-2">
                {requirements?.emailAddressRequired && (
                  <div className="flex gap-3 items-center p-4 rounded-2xl border border-neutral-100 bg-neutral-50">
                    <div className="flex justify-center items-center w-9 h-9 text-emerald-600 bg-white rounded-xl border border-neutral-100">
                      <CheckCircleOutline />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-neutral-900">
                        Email address
                      </p>
                      <p className="text-xs text-neutral-500">Required</p>
                    </div>
                  </div>
                )}
                {requirements?.mobilePhoneNumberRequired && (
                  <div className="flex gap-3 items-center p-4 rounded-2xl border border-neutral-100 bg-neutral-50">
                    <div className="flex justify-center items-center w-9 h-9 text-emerald-600 bg-white rounded-xl border border-neutral-100">
                      <CheckCircleOutline />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-neutral-900">
                        Mobile phone
                      </p>
                      <p className="text-xs text-neutral-500">Required</p>
                    </div>
                  </div>
                )}
                {requirements?.travelerRequirements?.[0]?.documentRequired && (
                  <div className="flex gap-3 items-center p-4 rounded-2xl border border-neutral-100 bg-neutral-50">
                    <div className="flex justify-center items-center w-9 h-9 text-amber-600 bg-white rounded-xl border border-neutral-100">
                      <Description />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-neutral-900">
                        Passport / ID
                      </p>
                      <p className="text-xs text-neutral-500">Required</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sticky Price Summary Column */}
          <div className="h-96 lg:col-span-1">
            <div className="shadow-lg card">
              <div className="p-3 text-center border-b border-solid border-x-gray-400">
                <div className="row">
                  <div className="col-md-6">
                    <h3 className="card-title">Flight Cost</h3>
                  </div>
                  <div className="col-md-6">
                    <h3 className="card-title">
                      {formatPrice(price?.grandTotal || price?.total)}{" "}
                      {price?.currency}
                    </h3>
                  </div>
                </div>
              </div>
              <div className="card-body">
                <div className="flex flex-row justify-around text-black">
                  <div className="">
                    <p>Base Fare </p>
                    <p>Service Charge </p>
                    <p>Taxes and Fees </p>
                  </div>
                  <div className="">
                    {price?.base && (
                      <p>
                        {formatPrice(price?.base)} {price?.currency}
                      </p>
                    )}
                    <p>
                      {formatPrice(charges || 0)} {price?.currency}
                    </p>

                    {travelerPricings?.length > 0 && (
                      <p>
                        {formatPrice(
                          travelerPricings?.reduce((sum, tp) => {
                            const taxAmount =
                              tp.price.taxes?.reduce(
                                (taxSum, tax) =>
                                  taxSum + parseFloat(tax.amount || 0),
                                0,
                              ) || 0;
                            return sum + taxAmount;
                          }, 0),
                        )}{" "}
                        {price?.currency}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="card-footer">
                <div className="justify-end d-flex">
                  <div className="text-xl font-bold text-black">
                    Total Price:{" "}
                    {formatPrice(
                      (+price?.grandTotal || +price?.total) + (+charges || 0),
                    )}{" "}
                    {price.currency}
                  </div>
                </div>
              </div>
            </div>
            <div className="">
              <ButtonPrimary
                className="w-full !rounded-2xl  font-medium"
                onClick={handleContinue}
              >
                Continue to booking
              </ButtonPrimary>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AmadeusFlightPricing;
